import { useState, MouseEvent, ReactNode, useRef } from "react";
import { IconButton, Popover, Typography, Box, useMediaQuery } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { Close } from "@mui/icons-material";

type Props = {
  title?: string;
  children: ReactNode;
  hoverCloseDelay?: number; // milliseconds
};

/**
 * Info Icon with Popover
 * Desktop: opens on hover → quick info without clicks
 * Mobile / touch devices: opens on click → readable long text
 * Keyboard accessible: can be triggered via focus if wrapped in a button
 * Supports long translations like your “settings help text”
 */
export function InfoIconWithPopover({ title, children, hoverCloseDelay = 200 }: Readonly<Props>) {
  const isTouchDevice = useMediaQuery("(hover: none)"); // Detect touch devices

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const open = Boolean(anchorEl);

  /* ---------- shared helpers ---------- */

  const clearCloseTimeout = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  };

  const openPopover = (el: HTMLElement) => {
    clearCloseTimeout();
    setAnchorEl(el);
  };

  const closePopover = () => {
    clearCloseTimeout();
    closeTimeout.current = setTimeout(() => {
      setAnchorEl(null);
    }, hoverCloseDelay);
  };

  /* ---------- desktop hover ---------- */

  const handleMouseEnterIcon = (e: MouseEvent<HTMLElement>) => {
    if (!isTouchDevice) {
      openPopover(e.currentTarget);
    }
  };

  const handleDeskTopClose = () => {
    if (!isTouchDevice) {
      closePopover();
    }
  };

  /* ---------- mobile click ---------- */

  const handleClickIcon = (e: MouseEvent<HTMLElement>) => {
    if (isTouchDevice) {
      setAnchorEl(prev => (prev ? null : e.currentTarget));
    }
  };

  /* ---------- popover ---------- */

  const handlePopoverMouseEnter = () => {
    if (!isTouchDevice) {
      clearCloseTimeout();
    }
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label="Info"
        data-testid="info-icon-with-popover-iconButton"
        onClick={handleClickIcon}
        onMouseEnter={handleMouseEnterIcon}
        onMouseLeave={handleDeskTopClose}
      >
        <InfoIcon sx={{ fontSize: "medium" }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        disableRestoreFocus
        onClose={(_, reason) => {
          if (isTouchDevice || reason === "escapeKeyDown") {
            setAnchorEl(null);
          }
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            "data-testid": "info-icon-with-popover-paper",
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handleDeskTopClose
          } as any
        }}
        sx={{ pointerEvents: "none", maxWidth: 1200 }}
      >
        <Box sx={{ p: 2, pointerEvents: "auto" }} data-testid="info-icon-with-popover-contents-box">
          {title && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography fontWeight={600} gutterBottom>
                {title}
              </Typography>

              {isTouchDevice && (
                <IconButton onClick={() => setAnchorEl(null)} data-testid="handleCloseOnMobile-btn">
                  <Close />
                </IconButton>
              )}
            </Box>
          )}
          <Typography variant="body1">{children}</Typography>
        </Box>
      </Popover>
    </>
  );
}
